if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const formData = new FormData(this.form);
        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const initials = String(formData.get('properties[Initial letters]') || '')
          .toUpperCase()
          .replace(/[^A-Z]/g, '')
          .slice(0, 3);
        const initialAddonQuantity = initials.length * quantity;
        const customizationToken =
          initialAddonQuantity > 0
            ? globalThis.crypto?.randomUUID?.() || `jeieam-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
            : '';

        if (customizationToken) {
          formData.append('properties[_Customization token]', customizationToken);
        }

        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        try {
          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];

          if (this.cart && initialAddonQuantity === 0) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
          }

          if (this.cart) this.cart.setActiveElement(document.activeElement);
          config.body = formData;

          let response = await fetch(`${routes.cart_add_url}`, config).then((result) => result.json());

          if (response.status) {
            this.handleAddError(response, variantId, linesUpdateDeferred);
            return;
          }

          if (initialAddonQuantity > 0) {
            const addonConfig = fetchConfig('javascript');
            addonConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
            delete addonConfig.headers['Content-Type'];

            const addonFormData = new FormData();
            addonFormData.append('id', '53993631154470');
            addonFormData.append('quantity', String(initialAddonQuantity));
            addonFormData.append('properties[_Initial letters]', initials);
            addonFormData.append('properties[_Linked jewelry variant]', String(variantId));
            addonFormData.append('properties[_Customization token]', customizationToken);

            if (this.cart) {
              addonFormData.append(
                'sections',
                this.cart.getSectionsToRender().map((section) => section.id)
              );
              addonFormData.append('sections_url', window.location.pathname);
            }

            addonConfig.body = addonFormData;
            const addonResponse = await fetch(`${routes.cart_add_url}`, addonConfig).then((result) => result.json());

            if (addonResponse.status) {
              const cleanupCart = await fetch(`${routes.cart_url}.json`).then((result) => result.json());
              const cleanupUpdates = {};

              cleanupCart.items.forEach((item) => {
                if (item.properties?.['_Customization token'] === customizationToken) {
                  cleanupUpdates[item.key] = 0;
                }
              });

              if (Object.keys(cleanupUpdates).length > 0) {
                await fetch(`${routes.cart_update_url}`, {
                  ...fetchConfig(),
                  body: JSON.stringify({ updates: cleanupUpdates }),
                });
              }

              throw new Error(addonResponse.description || addonResponse.message || 'Unable to add initial customization.');
            }

            response = addonResponse;
          }

          if (!this.cart) {
            this.resolveCartLinesUpdate(linesUpdateDeferred);
            window.location = window.routes.cart_url;
            return;
          }

          this.resolveCartLinesUpdate(linesUpdateDeferred);

          const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
          if (!this.error)
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: variantId,
              cartData: response,
            }).then(() => {
              CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
            });

          this.error = false;
          const quickAddModal = this.closest('quick-add-modal');

          if (quickAddModal) {
            document.body.addEventListener(
              'modalClosed',
              () => {
                setTimeout(() => {
                  CartPerformance.measure('add:paint-updated-sections', () => {
                    this.cart.renderContents(response);
                  });
                });
              },
              { once: true }
            );
            quickAddModal.hide(true);
          } else {
            CartPerformance.measure('add:paint-updated-sections', () => {
              this.cart.renderContents(response);
            });
          }
        } catch (e) {
          console.error(e);
          this.handleErrorMessage(e.message || 'Unable to add customization. Please try again.');
          this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
          linesUpdateDeferred?.reject(e);
          this.error = true;
        } finally {
          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading__spinner').classList.add('hidden');

          CartPerformance.measureFromEvent('add:user-action', evt);
        }
      }

      handleAddError(response, variantId, linesUpdateDeferred) {
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'product-form',
          productVariantId: variantId,
          errors: response.errors || response.description,
          message: response.message,
        });

        this.handleErrorMessage(response.description);
        this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
        linesUpdateDeferred?.reject(new Error(response.description || response.message));

        const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
        if (!soldOutMessage) {
          this.error = true;
          return;
        }

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButtonText.classList.add('hidden');
        soldOutMessage.classList.remove('hidden');
        this.error = true;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise =
          typeof CartItems !== 'undefined'
            ? CartItems.fetchCartData()
            : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
